import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('sv-SE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
}

export function formatTime(timeStr) {
  return timeStr || '';
}

export function formatDateTime(dateStr, timeStr) {
  return `${formatDate(dateStr)} ${formatTime(timeStr)}`;
}

export function getInitials(name) {
  if (!name) return '??';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Build a compact breakdown string for challenge points, only showing sources > 0.
// Example: formatPointsBreakdown({attendance_points:15, evaluation_points:10, praise_points:4, manual_points:5})
//   => "15p närvaro • 10p utvärderingar • 4p beröm • 5p manuellt"
export function formatPointsBreakdown(player) {
  if (!player) return '';
  const parts = [];
  if (player.attendance_points > 0) parts.push(`${player.attendance_points}p närvaro`);
  if (player.evaluation_points > 0) parts.push(`${player.evaluation_points}p utvärderingar`);
  if (player.praise_points > 0) parts.push(`${player.praise_points}p beröm`);
  if (player.manual_points > 0) parts.push(`${player.manual_points}p manuellt`);
  return parts.join(' • ');
}

// Default event type colors — Master Calendar visual identity
// training=green, match=Trainova gold, cup=purple, meeting=blue, other=neutral grey
export const EVENT_TYPE_COLORS = {
  training: '#22C55E',
  match: '#3B82F6',
  cup: '#8B5CF6',
  meeting: '#D4A74A',
  other: '#6B7280'
};

export function getEventTypeColor(type, customColor = null) {
  if (customColor) return customColor;
  return EVENT_TYPE_COLORS[type] || EVENT_TYPE_COLORS.other;
}

export function getEventTypeBgClass(type) {
  switch (type) {
    case 'training':
      return "bg-[#22C55E]/20 text-[rgb(var(--tn-text-22c55e))] border-[#22C55E]/30";
    case 'match':
      return "bg-[#3B82F6]/20 text-[rgb(var(--tn-text-3b82f6))] border-[#3B82F6]/30";
    case 'cup':
      return "bg-[#8B5CF6]/20 text-[rgb(var(--tn-text-8b5cf6))] border-[#8B5CF6]/30";
    case 'meeting':
      return "bg-[#D4A74A]/20 text-[rgb(var(--tn-text-d4a74a))] border-[#D4A74A]/30";
    default:
      return "bg-[rgb(var(--tn-bg-6b7280))]/20 text-[rgb(var(--tn-text-9ca3af))] border-[rgb(var(--tn-border-6b7280))]/30";
  }
}

export function getEventTypeLabel(type) {
  switch (type) {
    case 'training':
      return 'Träning';
    case 'match':
      return 'Match';
    case 'meeting':
      return 'Möte';
    default:
      return 'Övrigt';
  }
}

export function getRsvpStatusColor(status) {
  switch (status) {
    case 'attending':
      return 'bg-primary/20 text-primary';
    case 'not_attending':
      return 'bg-destructive/20 text-destructive';
    case 'maybe':
      return 'bg-accent/20 text-accent';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

export function getRsvpStatusLabel(status) {
  switch (status) {
    case 'attending':
      return 'Kommer';
    case 'not_attending':
      return 'Kommer ej';
    case 'maybe':
      return 'Osäker';
    default:
      return 'Ej svarat';
  }
}

export function getBlockTypeLabel(type) {
  const labels = {
    samling: 'Samling',
    uppvarmning: 'Uppvärmning',
    tema: 'Tema',
    spel: 'Spel',
    fys: 'Fys',
    teknik: 'Teknikstation',
    nedvarvning: 'Nedvarvning',
    ovning: 'Övning'
  };
  return labels[type] || type;
}

export function getBlockTypeColor(type) {
  const colors = {
    samling: "border-l-[rgb(var(--tn-border-6b7280))]",
    uppvarmning: 'border-l-orange-500',
    tema: 'border-l-primary',
    spel: 'border-l-secondary',
    fys: 'border-l-red-500',
    teknik: 'border-l-purple-500',
    nedvarvning: 'border-l-cyan-500',
    ovning: 'border-l-yellow-500'
  };
  return colors[type] || "border-l-[rgb(var(--tn-border-6b7280))]";
}

export function getFocusTagColor(tag) {
  const colors = {
    Teknik: "bg-purple-500/20 text-[rgb(var(--tn-text-c084fc))] border-purple-500/30",
    Taktik: "bg-blue-500/20 text-[rgb(var(--tn-text-60a5fa))] border-blue-500/30",
    Fysik: "bg-red-500/20 text-[rgb(var(--tn-text-f87171))] border-red-500/30",
    Mentalt: "bg-yellow-500/20 text-[rgb(var(--tn-text-facc15))] border-yellow-500/30"
  };
  return colors[tag] || "bg-[rgb(var(--tn-bg-6b7280))]/20 text-[rgb(var(--tn-text-9ca3af))] border-[rgb(var(--tn-border-6b7280))]/30";
}

// Game format helpers
export const GAME_FORMATS = [
  { id: '5v5', label: '5 mot 5', players: 5 },
  { id: '7v7', label: '7 mot 7', players: 7 },
  { id: '9v9', label: '9 mot 9', players: 9 },
  { id: '11v11', label: '11 mot 11', players: 11 }
];

export function getGameFormatPlayers(format) {
  const f = GAME_FORMATS.find(g => g.id === format);
  return f?.players || 11;
}

// Default widget types for dashboard
export const WIDGET_TYPES = [
  { id: 'next-training', label: 'Nästa träning', size: 'medium' },
  { id: 'next-match', label: 'Nästa match', size: 'small' },
  { id: 'todo', label: 'Att göra', size: 'medium' },
  { id: 'calendar', label: 'Kalender', size: 'large' },
  { id: 'upcoming', label: 'Kommande aktiviteter', size: 'medium' },
  { id: 'statistics', label: 'Statistik', size: 'medium' },
  { id: 'attendance', label: 'Närvaro', size: 'medium' },
  { id: 'chat', label: 'Senaste meddelanden', size: 'medium' },
  { id: 'team', label: 'Trupp', size: 'small' },
  { id: 'quick-stats', label: 'Snabbstatistik', size: 'small' }
];
